import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faXmark } from '@fortawesome/free-solid-svg-icons';
import { authService } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const SetPasswordBanner: React.FC = () => {
  const { user, clearPasswordSetup } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (!user?.needsPasswordSetup || dismissed) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authService.setPassword(password);
      clearPasswordSetup();
      setPassword('');
      setConfirmPassword('');
      setExpanded(false);
      toast.success('Password saved. You can use it to sign in next time.');
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to set password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="dashboard-set-password-banner" aria-label="Set password reminder">
      <div className="dashboard-set-password-banner__header">
        <div className="dashboard-set-password-banner__title">
          <FontAwesomeIcon icon={faKey} className="dashboard-set-password-banner__icon" />
          <div>
            <strong>Please set a password for login</strong>
            <p>
              You signed in with a one-time link. Set a password so you can sign in with email next time.
            </p>
          </div>
        </div>
        <div className="dashboard-set-password-banner__actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Hide' : 'Set password'}
          </button>
          <button
            type="button"
            className="dashboard-set-password-banner__dismiss"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss banner"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>

      {expanded && (
        <form className="dashboard-set-password-banner__form" onSubmit={handleSubmit}>
          <div className="dashboard-set-password-banner__fields">
            <label>
              New password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                autoComplete="new-password"
                required
              />
            </label>
            <label>
              Confirm password
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={6}
                autoComplete="new-password"
                required
              />
            </label>
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
            {loading ? 'Saving...' : 'Save password'}
          </button>
        </form>
      )}
    </section>
  );
};

export default SetPasswordBanner;
